// DEPLOY: /var/www/woody.wtf/frontend/src/components/tradingShared.js
// Shared by the Trading section pages (Portfolio, Trade, and later Options, Orders, Currency):
// styles, number formatting, tables, ticker search, the broker-account hook (admin guard, connection,
// gateway status, chosen account) and the status bar. All data goes through /api/trading (trading.py).
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api';

export const s = {
  page:      { minHeight: '100vh', background: 'var(--bg)' },
  content:   { maxWidth: '1300px', margin: '0 auto', padding: '40px 32px' },
  heading:   { fontSize: '22px', fontWeight: 600, letterSpacing: '-0.02em', marginBottom: '16px' },
  card:      { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'calc(var(--radius) * 2)', padding: '20px 24px', marginBottom: '20px' },
  title:     { fontSize: '14px', fontWeight: 600, color: 'var(--text)' },
  meta:      { fontSize: '12px', color: 'var(--text-muted)' },
  btn:       { padding: '6px 14px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', cursor: 'pointer', fontSize: '12px', fontWeight: 500, fontFamily: "'DM Sans', sans-serif", background: 'var(--surface)', color: 'var(--text)' },
  err:       { color: 'var(--error)', fontSize: '13px', marginTop: '8px' },
  ok:        { color: 'var(--success)', fontSize: '13px', marginTop: '8px' },
  label:     { fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' },
  select:    { padding: '6px 10px', fontSize: '13px', fontFamily: "'DM Sans', sans-serif", border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', outline: 'none' },
  input:     { width: '100%', boxSizing: 'border-box', padding: '6px 8px', fontSize: '13px', fontFamily: "'DM Sans', sans-serif", border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', outline: 'none' },
  table:     { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th:        { textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)', fontWeight: 500, fontFamily: "'DM Mono', monospace", fontSize: '10px', letterSpacing: '0.05em', textTransform: 'uppercase', whiteSpace: 'nowrap' },
  td:        { padding: '8px 10px', borderBottom: '1px solid var(--border)', verticalAlign: 'middle', whiteSpace: 'nowrap', fontFamily: "'DM Mono', monospace" },
  link:      { color: 'var(--accent)', cursor: 'pointer', fontSize: '12px', textDecoration: 'none' },
  mono:      { fontFamily: "'DM Mono', monospace" },
  badge:     live => ({ display: 'inline-block', padding: '2px 8px', borderRadius: 'var(--radius)', fontSize: '11px', fontWeight: 600,
                        background: live ? 'var(--error)' : 'var(--accent)', color: '#fff' }),
  tiles:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: '12px', marginBottom: '20px' },
  tile:      { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'calc(var(--radius) * 2)', padding: '14px 16px' },
  tileLabel: { fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: "'DM Mono', monospace" },
  tileValue: { fontSize: '20px', fontWeight: 600, fontFamily: "'DM Mono', monospace", marginTop: '4px', letterSpacing: '-0.02em' },
  tileSub:   { fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' },
  tab:       on => ({ padding: '8px 14px', fontSize: '13px', cursor: 'pointer', border: 'none', background: 'none', fontFamily: "'DM Sans', sans-serif",
                      fontWeight: on ? 600 : 500, color: on ? 'var(--accent)' : 'var(--text-muted)',
                      borderBottom: `2px solid ${on ? 'var(--accent)' : 'transparent'}`, marginBottom: '-1px' }),
  count:     { marginLeft: '6px', padding: '0 6px', borderRadius: '10px', fontSize: '11px', background: 'var(--accent-dim)', color: 'var(--accent)' },
  pill:      (bg, fg) => ({ display: 'inline-block', padding: '1px 8px', borderRadius: '10px', fontSize: '11px', fontFamily: "'DM Sans', sans-serif", background: bg, color: fg }),
  seg:       on => ({ flex: 1, padding: '7px 0', fontSize: '12px', fontWeight: 600, cursor: 'pointer', border: '1px solid var(--border)', fontFamily: "'DM Sans', sans-serif",
                      background: on ? 'var(--accent)' : 'var(--surface)', color: on ? '#fff' : 'var(--text-muted)' }),
};

export const REFRESH_MS = 5000;
const SEARCH_DEBOUNCE = 250;
export const OPEN_STATUSES = ['ApiPending', 'PendingSubmit', 'PreSubmitted', 'Submitted'];   // IBKR statuses that can still be changed

export const isNum = v => v !== null && v !== undefined && v !== '' && !isNaN(Number(v));
export const num = (v, dp = 2) => isNum(v) ? Number(v).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp }) : '—';
export const signed = (v, dp = 2) => isNum(v) ? `${Number(v) > 0 ? '+' : ''}${num(v, dp)}` : '—';
export const tone = v => !isNum(v) || Number(v) === 0 ? 'var(--text)' : Number(v) > 0 ? 'var(--success)' : 'var(--error)';
export const arrow = v => !isNum(v) || Number(v) === 0 ? '' : Number(v) > 0 ? '▲ ' : '▼ ';
export const qty = v => isNum(v) ? Number(v).toLocaleString(undefined, { maximumFractionDigits: 4 }) : '—';
export const price = v => isNum(v) ? num(v, Number(v) < 1 ? 4 : Number(v) < 10 ? 3 : 2) : '—';
export const time = iso => iso ? new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';
export const show = v => v === null || v === undefined ? '—' : Array.isArray(v) ? v.join(', ') : typeof v === 'object' ? JSON.stringify(v) : String(v);
export const chartTicker = code => { const [m, ...r] = code.split('.'); return `${r.join('.')}:${m}`; };   // US.AAPL -> AAPL:US
export const errText = e => e.response?.data?.error || e.message;
export const nameOf = r => r.desc || r.code;                        // options show 'AAPL 2026-12-18 200 C'
export const isOption = code => (code || '').startsWith('OP.');

export function statusPill(st) {
  if (st === 'Filled') return s.pill('var(--success-dim)', 'var(--success)');
  if (['Cancelled', 'ApiCancelled', 'Inactive', 'rejected'].includes(st)) return s.pill('var(--error-dim)', 'var(--error)');
  if (OPEN_STATUSES.includes(st)) return s.pill('var(--accent-dim)', 'var(--accent)');
  return s.pill('var(--bg)', 'var(--text-muted)');
}

// Column layouts for order and fill tables (Portfolio, Orders pages).
export const ORDER_COLS = [
  { key: 'code', label: 'Code', render: nameOf },
  { key: 'side', label: 'Side', render: r => <span style={{ color: r.side === 'BUY' ? 'var(--success)' : 'var(--error)' }}>{r.side}</span> },
  { key: 'qty', label: 'Qty', num: true, render: r => qty(r.qty) },
  { key: 'order_type', label: 'Type' },
  { key: 'price', label: 'Limit', num: true, render: r => r.order_type === 'MKT' ? '—' : price(r.price) },
  { key: 'filled', label: 'Filled', num: true, render: r => `${qty(r.filled)} / ${qty((Number(r.filled) || 0) + (Number(r.remaining) || 0))}` },
  { key: 'avg_fill_price', label: 'Avg fill', num: true, render: r => price(r.avg_fill_price) },
  { key: 'order_status', label: 'Status', render: r => <span style={statusPill(r.order_status)}>{r.order_status}</span> },
  { key: 'tif', label: 'TIF' },
  { key: 'order_id', label: 'ID' },
];
export const FILL_COLS = [
  { key: 'time', label: 'Time', render: r => time(r.time) },
  { key: 'code', label: 'Code', render: nameOf },
  { key: 'side', label: 'Side', render: r => <span style={{ color: r.side === 'BOT' ? 'var(--success)' : 'var(--error)' }}>{r.side === 'BOT' ? 'BUY' : r.side === 'SLD' ? 'SELL' : r.side}</span> },
  { key: 'shares', label: 'Qty', num: true, render: r => qty(r.shares) },
  { key: 'price', label: 'Price', num: true, render: r => price(r.price) },
  { key: 'commission', label: 'Commission', num: true, render: r => num(r.commission) },
  { key: 'realized_pnl', label: 'Realised', num: true, render: r => <span style={{ color: tone(r.realized_pnl) }}>{signed(r.realized_pnl)}</span> },
  { key: 'exchange', label: 'Exchange' },
  { key: 'order_id', label: 'Order' },
];

// Table from column defs: {key, label, num (right-align), render(row)}; action(row) adds a last column.
export function Table({ cols, rows, empty, action }) {
  if (!rows?.length) return <div style={{ ...s.meta, padding: '16px 0' }}>{empty}</div>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={s.table}>
        <thead><tr>
          {cols.map(c => <th key={c.key} style={{ ...s.th, textAlign: c.num ? 'right' : 'left' }}>{c.label}</th>)}
          {action && <th style={s.th}></th>}
        </tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={i}>
            {cols.map(c => <td key={c.key} style={{ ...s.td, textAlign: c.num ? 'right' : 'left' }}>{c.render ? c.render(r) : show(r[c.key])}</td>)}
            {action && <td style={{ ...s.td, textAlign: 'right' }}>{action(r)}</td>}
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

// Any list of records, every column.
export function DataTable({ rows, empty }) {
  if (!rows?.length) return <div style={{ ...s.meta, padding: '16px 0' }}>{empty}</div>;
  return <Table cols={Object.keys(rows[0]).map(k => ({ key: k, label: k }))} rows={rows} empty={empty} />;
}

// One record as label / value pairs, in columns.
export function KeyValues({ obj }) {
  if (!obj) return null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '2px 24px', fontSize: '12px' }}>
      {Object.entries(obj).map(([k, v]) => (
        <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '4px 0', borderBottom: '1px solid var(--border)' }}>
          <span style={{ color: 'var(--text-muted)' }}>{k}</span>
          <span style={{ ...s.mono, textAlign: 'right' }}>{show(v)}</span>
        </div>
      ))}
    </div>
  );
}

export function Tile({ label, value, sub, color }) {
  return (
    <div style={s.tile}>
      <div style={s.tileLabel}>{label}</div>
      <div style={{ ...s.tileValue, color: color || 'var(--text)' }}>{value}</div>
      {sub && <div style={s.tileSub}>{sub}</div>}
    </div>
  );
}

// Admin guard, the user's broker connection, gateway status and the chosen account.
export function useTradingAccount() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [conns, setConns]   = useState(null);
  const [broker, setBroker] = useState('');
  const [status, setStatus] = useState(null);
  const [accId, setAccId]   = useState('');
  const [err, setErr]       = useState('');

  useEffect(() => {
    if (user && !user.is_admin) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    api.get('/trading/connections')
      .then(r => { setConns(r.data.connections); setBroker(r.data.connections[0]?.broker || ''); })
      .catch(e => setErr(errText(e)));
  }, []);

  const loadStatus = useCallback(async () => {
    if (!broker) return;
    setErr('');
    try {
      const r = (await api.get(`/trading/${broker}/status`)).data;
      setStatus(r);
      setAccId(id => id || r.connection.live_acc_id || r.accounts[0]?.acc_id || '');
    } catch (e) { setErr(errText(e)); }
  }, [broker]);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const acc = status?.accounts.find(a => a.acc_id === accId);
  return { conns, broker, status, acc, accId, setAccId, err, loadStatus };
}

// Connection dot, account (picker if several), LIVE badge, gateway details.
export function StatusBar({ t }) {
  const { conns, broker, status, acc, accId, setAccId, err, loadStatus } = t;
  const [svcBusy, setSvcBusy] = useState('');
  const [svcErr, setSvcErr] = useState('');
  const [svcMsg, setSvcMsg] = useState('');
  const c = status?.connection, g = status?.gateway, up = !!g?.connected;
  const service = status?.service, serviceUp = service === 'active';

  const serviceAction = async action => {
    if (action === 'stop' && !window.confirm('Stop IB Gateway? Trading pages will lose IBKR access until it is started again.')) return;
    if (action === 'restart' && !window.confirm('Restart IB Gateway? The trading connection will briefly disconnect and may require IBKR Mobile 2FA.')) return;
    setSvcBusy(action); setSvcErr(''); setSvcMsg('');
    try {
      const r = await api.post(`/trading/${broker}/gateway-service`, { action });
      setSvcMsg(r.data.message);
      await loadStatus();
    } catch (e) { setSvcErr(errText(e)); }
    finally { setSvcBusy(''); }
  };

  return (
    <>
      {err && <div style={{ ...s.err, marginBottom: '12px' }}>✕ {err}</div>}
      {conns && conns.length === 0 && <div style={s.meta}>No broker connection is set up for your account.</div>}
      {c && (
        <div style={{ ...s.card, padding: '12px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
            <span style={{ width: '9px', height: '9px', borderRadius: '50%', background: up ? 'var(--success)' : 'var(--error)' }} />
            <span style={{ fontSize: '13px', fontWeight: 500 }}>{up ? 'IB Gateway connected' : g ? 'IB Gateway not connected' : 'IBKR bridge not reachable'}</span>
            {status.accounts.length > 1 ? (
              <select style={s.select} value={accId} onChange={e => setAccId(e.target.value)}>
                {status.accounts.map(a => <option key={a.acc_id}>{a.acc_id}</option>)}
              </select>
            ) : acc && <span style={{ ...s.mono, fontSize: '13px' }}>{acc.acc_id}</span>}
            {acc && <span style={s.badge(acc.trd_env === 'REAL')}>{acc.trd_env === 'REAL' ? 'LIVE' : 'PAPER'}</span>}
            {service && <span style={{ ...s.meta }}>service: {service}</span>}
            <span style={{ ...s.meta, marginLeft: 'auto' }}>{c.broker.toUpperCase()} · {c.host}:{c.port}</span>
            {c.broker === 'ibkr' && <>
              <button style={s.btn} onClick={() => serviceAction(serviceUp ? 'stop' : 'start')} disabled={!!svcBusy}>
                {svcBusy === (serviceUp ? 'stop' : 'start') ? 'Working…' : serviceUp ? 'Stop' : 'Start'}
              </button>
              <button style={s.btn} onClick={() => serviceAction('restart')} disabled={!!svcBusy}>
                {svcBusy === 'restart' ? 'Working…' : 'Restart'}
              </button>
            </>}
            <button style={s.btn} onClick={loadStatus} disabled={!!svcBusy}>Refresh</button>
          </div>
          {status.error && <div style={s.err}>✕ {status.error}</div>}
          {svcErr && <div style={s.err}>✕ {svcErr}</div>}
          {svcMsg && <div style={s.ok}>{svcMsg}</div>}
          {g && (
            <details style={{ marginTop: '8px' }}>
              <summary style={{ cursor: 'pointer', fontSize: '12px', color: 'var(--text-muted)' }}>Gateway details</summary>
              <div style={{ marginTop: '8px' }}><KeyValues obj={{ ...g, settings_updated: c.updated_at }} /></div>
            </details>
          )}
        </div>
      )}
    </>
  );
}

// The account's data from the bridge, reloaded every REFRESH_MS. currency: balances currency.
export function useAccountData(broker, acc, currency = 'AUD') {
  const [data, setData]       = useState(null);
  const [err, setErr]         = useState('');
  const [updated, setUpdated] = useState(null);
  const load = useCallback(async () => {
    if (!broker || !acc) return;
    try {
      setData((await api.get(`/trading/${broker}/account/${acc.acc_id}`, { params: { currency } })).data);
      setUpdated(new Date()); setErr('');
    } catch (e) { setErr(errText(e)); }
  }, [broker, acc, currency]);
  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);
  return { data, err, updated, reload: load };
}

// Ticker search from our instruments table (/api/chart/search), AU and US only. onPick(code).
export function SymbolSearch({ onPick }) {
  const [q, setQ]       = useState('');
  const [res, setRes]   = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  const search = v => {
    if (timer.current) clearTimeout(timer.current);
    if (!v.trim()) { setRes([]); setOpen(false); return; }
    timer.current = setTimeout(() => {
      api.get(`/chart/search?q=${encodeURIComponent(v.trim())}`)
        .then(r => { const rows = r.data.filter(x => ['US', 'AU'].includes(x.exchange)); setRes(rows); setOpen(rows.length > 0); })
        .catch(() => {});
    }, SEARCH_DEBOUNCE);
  };
  const pick = r => { setQ(''); setRes([]); setOpen(false); onPick(`${r.exchange}.${r.symbol}`); };
  const onKey = e => {
    if (e.key !== 'Enter') return;
    const v = q.trim().toUpperCase();
    if (/^(US|AU)\.[A-Z0-9.-]+$/.test(v)) { setQ(''); setOpen(false); onPick(v); }   // typed a full code
    else if (res.length) pick(res[0]);
  };

  return (
    <div style={{ position: 'relative' }}>
      <input style={{ ...s.input, padding: '10px 12px', fontSize: '14px' }} value={q} placeholder="Search a company or ticker, or type US.AAPL / AU.BHP"
             onChange={e => { setQ(e.target.value); search(e.target.value); }} onKeyDown={onKey}
             onFocus={() => res.length && setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} spellCheck={false} />
      {open && (
        <div style={{ position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, marginTop: '4px', background: 'var(--surface)',
                      border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)', maxHeight: '320px', overflowY: 'auto' }}>
          {res.map(r => (
            <div key={`${r.symbol}:${r.exchange}`} onMouseDown={() => pick(r)}
                 style={{ display: 'flex', gap: '10px', alignItems: 'center', padding: '8px 12px', cursor: 'pointer', fontSize: '13px' }}
                 onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg)'; }} onMouseLeave={e => { e.currentTarget.style.background = 'var(--surface)'; }}>
              <span style={{ ...s.mono, fontWeight: 500, minWidth: '70px' }}>{r.symbol}</span>
              <span style={{ flex: 1, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
              <span style={s.pill('var(--accent-dim)', 'var(--accent)')}>{r.exchange === 'AU' ? 'ASX' : 'US'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Download rows as a CSV file (every field of each row, header from the first row).
export function downloadCsv(filename, rows) {
  if (!rows?.length) return;
  const cols = Object.keys(rows[0]);
  const cell = v => { const x = v === null || v === undefined ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);
                      return /[",\n]/.test(x) ? `"${x.replace(/"/g, '""')}"` : x; };
  const csv = [cols.join(','), ...rows.map(r => cols.map(c => cell(r[c])).join(','))].join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}
